import type { IntegrationLoginProvider } from '~kui/types/auth';

import type { ProviderLoginPayloadFieldsFragment } from '~/graphql/generated/graphql';

import { EVENTS } from '~/constants/events';
import { COACH_ROUTE_INDEX } from '~/constants/routes/coach';
import { UNIFIED_ROUTE_AUTH_FAILED, UNIFIED_ROUTE_AUTH_PROOF, UNIFIED_ROUTE_LOGIN } from '~/constants/routes/unified';
import {
  completeProviderLoginMutation,
  completeProviderLoginProofMutation,
  providerLoginPayloadFieldsFragment,
  startProviderLoginMutation,
} from '~/graphql/documents/core';
import { useFragment } from '~/graphql/generated/fragment-masking';
import { ProviderLoginFailureReason, SiteRoleType } from '~/graphql/generated/graphql';
import { loginResumeQuery, STEP_CHOOSE_ACCOUNT } from '~/types/login';
import { providerLoginChoiceQuery, providerLoginFailureLocation, PROVIDERS } from '~/utils/providerLogin';
import { isSafeRedirectPath } from '~/utils/redirect';

/** Every provider login mutation answers with the same payload, selected through one fragment. */
type ProviderLoginOutcome = ProviderLoginPayloadFieldsFragment;
type ProviderLoginFailure = Extract<ProviderLoginOutcome, { __typename: 'ProviderLoginFailure' }>;
type ProviderLoginProofRequired = Extract<ProviderLoginOutcome, { __typename: 'ProviderLoginProofRequired' }>;
type ProviderLoginAccountChoiceRequired = Extract<ProviderLoginOutcome, { __typename: 'ProviderLoginAccountChoiceRequired' }>;

/** What the proof page needs that the URL does not carry, kept in history state. */
export type ProviderLoginProofState = Pick<ProviderLoginProofRequired, 'maskedAddress' | 'launchedFromProvider'>;

/**
 * Whether the server checked a proof code, and what it found. ProviderLoginService::completeProof
 * asks the gates and finds the attempt's code before it checks the code, so an expired login or a
 * closed connection says nothing about it. On the proof path a sign-in failure is only the last
 * wrong code, and a right code can still end in needs attention or not enrolled.
 */
const judgedProofCode = (outcome: ProviderLoginOutcome | undefined): 'yes' | 'no' | null => {
  if (outcome?.__typename === 'ProviderLoginProofRequired') {
    return outcome.codeRejected ? 'no' : null;
  }
  if (outcome?.__typename === 'ProviderLoginSignedIn' || outcome?.__typename === 'ProviderLoginAccountChoiceRequired') {
    return 'yes';
  }
  if (outcome?.__typename === 'ProviderLoginFailure') {
    if (outcome.screen === ProviderLoginFailureReason.SignInFailed) {
      return 'no';
    }
    return outcome.screen === ProviderLoginFailureReason.NeedsAttention || outcome.screen === ProviderLoginFailureReason.NotEnrolled ? 'yes' : null;
  }
  return null;
};

/**
 * The schools integration login on the client: the login button starts it, the provider login
 * page completes it. Both mutations answer with the next step, which this follows.
 */
export const useIntegrationLogin = () => {
  const route = useRoute();
  const client = useUrqlClient();
  const { clearClientSession, completeLoginProcess } = useAuth();
  const { $analytics } = useNuxtApp();

  /**
   * One failure page, its reason and the attempt id as the reference code. A request that never
   * reached the pipeline (offline, rate limited) has neither and shows the generic copy; when the
   * page knows the login was launched from the provider's portal, that copy still sends the
   * person back there.
   */
  const showFailure = async (failure: ProviderLoginFailure | undefined, launchedFromProvider: boolean) => {
    const location = failure
      ? providerLoginFailureLocation(failure)
      : { path: clusterRedirectPath(UNIFIED_ROUTE_AUTH_FAILED), query: launchedFromProvider ? { launch: 'provider' } : {} };

    await navigateTo(location, { replace: true });
  };

  /**
   * The server has replaced the session; whatever the last person left in this client (auth
   * store, cluster cookies, local storage) goes before the new session's first real render. On a
   * shared classroom device this is what keeps the last student's state off the next student's
   * screen. The analytics identity is kept, so the login identifies on the current device id like
   * every other login.
   */
  const land = async (provider: IntegrationLoginProvider, landingPath: string, viaProof: boolean) => {
    clearClientSession();
    await completeLoginProcess();

    $analytics.track(EVENTS.UNIFIED_AUTH_LOGIN_COMPLETED, {
      method: provider,
      roleType: SiteRoleType.Student,
      isNewUser: false,
      viaProof,
    });

    // Only a same-app path, never an absolute URL; the pipeline already chose the surface's
    // home when the return path did not belong to it.
    await navigateTo(clusterRedirectPath(isSafeRedirectPath(landingPath) ? landingPath : COACH_ROUTE_INDEX), { replace: true });
  };

  /**
   * The login waits for the code emailed to the address the provider asserted. The attempt id
   * rides in the URL (it is already the reference code on every failure page; the browser cookie
   * is the secret), the masked address and the launch in history state.
   */
  const askForProof = async (provider: IntegrationLoginProvider, outcome: ProviderLoginProofRequired) => {
    const state: ProviderLoginProofState = {
      maskedAddress: outcome.maskedAddress,
      launchedFromProvider: outcome.launchedFromProvider,
    };

    await navigateTo(
      { path: clusterRedirectPath(UNIFIED_ROUTE_AUTH_PROOF(provider)), query: { attempt: outcome.attemptId }, state },
      { replace: true },
    );
  };

  /**
   * The person has several school accounts, and the login page holds the choice between them.
   * Nothing is signed in yet, but the server has already ended any earlier session, so the client
   * forgets the last person now, before the login page renders. Once an account is picked the
   * login page lands on the path the pipeline chose, and reports the sign-in as this provider's.
   */
  const chooseAccount = async (
    provider: IntegrationLoginProvider,
    { email, landingPath }: ProviderLoginAccountChoiceRequired,
    viaProof: boolean,
  ) => {
    clearClientSession();

    await navigateTo(
      {
        path: clusterRedirectPath(UNIFIED_ROUTE_LOGIN),
        query: {
          ...loginResumeQuery({ email, step: STEP_CHOOSE_ACCOUNT }),
          ...providerLoginChoiceQuery({ provider, viaProof }),
          ...(isSafeRedirectPath(landingPath) ? { redirect: landingPath } : {}),
        },
      },
      { replace: true },
    );
  };

  const follow = async (
    provider: IntegrationLoginProvider,
    outcome: ProviderLoginOutcome | undefined,
    { launchedFromProvider = false, viaProof = false } = {},
  ) => {
    if (outcome?.__typename === 'ProviderLoginRedirect') {
      window.location.href = outcome.providerUrl;
      return;
    }
    if (outcome?.__typename === 'ProviderLoginSignedIn') {
      await land(provider, outcome.landingPath, viaProof);
      return;
    }
    if (outcome?.__typename === 'ProviderLoginAccountChoiceRequired') {
      await chooseAccount(provider, outcome, viaProof);
      return;
    }
    if (outcome?.__typename === 'ProviderLoginProofRequired') {
      await askForProof(provider, outcome);
      return;
    }

    await showFailure(outcome, launchedFromProvider);
  };

  /**
   * Step 0 of the login pipeline. The login page's `redirect` query rides along as the pipeline's
   * return path when it is a same-app path; otherwise the pipeline picks the surface's home. A
   * launch from the provider's portal reached the provider login page without our state and
   * starts here too.
   */
  const startIntegrationLogin = async (provider: IntegrationLoginProvider, { launchedFromProvider = false } = {}) => {
    const redirect = route.query.redirect;
    const { data } = await client.mutation(startProviderLoginMutation, {
      input: {
        provider: PROVIDERS[provider],
        returnUrl: !launchedFromProvider && isSafeRedirectPath(redirect) ? redirect : null,
        launchedFromProvider,
      },
    });

    await follow(provider, useFragment(providerLoginPayloadFieldsFragment, data?.core.auth.startProviderLogin), { launchedFromProvider });
  };

  /**
   * The rest of the login pipeline, with whatever the provider sent back to its redirect URI.
   */
  const completeIntegrationLogin = async (
    provider: IntegrationLoginProvider,
    { state, code, error }: { state: string; code: string | null; error: string | null },
  ) => {
    const { data } = await client.mutation(completeProviderLoginMutation, {
      input: { provider: PROVIDERS[provider], state, code, error },
    });

    await follow(provider, useFragment(providerLoginPayloadFieldsFragment, data?.core.auth.completeProviderLogin));
  };

  /**
   * The proof page's code. A wrong code keeps the student on the page; anything else ends the
   * login one way or the other.
   */
  const proveIntegrationLogin = async (
    provider: IntegrationLoginProvider,
    { attemptId, code, launchedFromProvider = false }: { attemptId: string; code: string; launchedFromProvider?: boolean },
  ) => {
    const { data } = await client.mutation(completeProviderLoginProofMutation, {
      input: { provider: PROVIDERS[provider], attemptId, code },
    });
    const outcome = useFragment(providerLoginPayloadFieldsFragment, data?.core.auth.completeProviderLoginProof);
    const correct = judgedProofCode(outcome);
    if (correct) {
      $analytics.track(EVENTS.LOGIN_OTP_SUBMITTED, { correct, method: provider });
    }
    if (outcome?.__typename === 'ProviderLoginProofRequired' && outcome.codeRejected) {
      return { codeRejected: true };
    }

    await follow(provider, outcome, { launchedFromProvider, viaProof: true });
    return { codeRejected: false };
  };

  return { startIntegrationLogin, completeIntegrationLogin, proveIntegrationLogin };
};
